import { SettingSidebarProps } from '#components/ui/SettingSidebar/SettingSidebar';
import { useGlobalContext } from '#contexts/GlobalContext';
import { INIT_FILTER_ACCOUNT } from '#pages/admin-settings/pages/account/context';
import { INITIAL_SORTER_ASSIGNED } from '#pages/assigned';
import { INITIAL_SORTER_ORDER } from '#pages/order';
import { INIT_FILTER_SHARE } from '#pages/share/context';
import { INIT_FILTER_LAYOUT } from '#pages/user-settings/pages/layout-report/context';
import matchRoute from '#routes/components/matchRoute';
import KeyOutlined from '@ant-design/icons/KeyOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { AccountOutlined, HelpOutlined, LoginOutlined, PowerOutlined } from '@vindr/icons';
import { useChangePassword, useUpdateProfile } from '@vindr/services/actions/account.action';
import { actionLogout, requestLogin } from '@vindr/services/actions/auth.action';
import { getAccounts } from '@vindr/services/apis/account.api';
import { getAssigns } from '@vindr/services/apis/assign.api';
import { getFiltersTree } from '@vindr/services/apis/filterManagement.api';
import { getLayouts } from '@vindr/services/apis/layout.api';
import { getOrderWorklist, getOrders } from '@vindr/services/apis/order.api';
import { getShares } from '@vindr/services/apis/share.api';
import { ParamsStudyList, fetchStudyList } from '@vindr/services/apis/study.api';
import { INIT_PAGINATION_PARAM } from '@vindr/services/constants/common.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import {
  INITIAL_FILTER_STUDYLIST,
  INITIAL_SORTER_STUDYLIST,
} from '@vindr/services/constants/study.constant';
import { Logo } from '@vindr/ui';
import { getPrefetch, prefetchTemplate } from '@vindr/ui/src/components/Templates/preload';
import Profile from '@vindr/ui/src/components/ui/UpdateProfile';
import lazyWithPreload from '@vindr/utils/LazyWithPreload';
import { isEmptyObject, sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import usePreloadLazy from '@vindr/utils/hooks/usePreloadLazy';
import { Dropdown, Menu } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { MenuProps } from 'rc-menu';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChangePassword from './ChangePassword';
import LanguageSwitcher from './LanguageSwitcher';
import ToggleSplitScreen from './ToggleSplitScreen';
import Username from './Username';
// import { INIT_FILTER_PERFORMANCE_BY_RADIOLOGIST } from '#pages/dashboard/pages/performance-by-radiologist/context';
// import { getDashboardPerformByRadiologist } from '@vindr/services/apis/dashboard';
// import { ThemeSwitcher } from '@vindr/ui';

const menuClass = 'header-menu';

type HeaderProps = {
  items?: MenuProps['items'] | SettingSidebarProps['items'];
  isSticky?: boolean;
};
const Header = ({ items, isSticky }: HeaderProps): JSX.Element => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('Vindoc');
  const { keycloak, message, iframe } = useGlobalContext();
  const canLogin = keycloak.initiated;
  const userPermissions = keycloak.getPermissions();
  const userInfo = keycloak.userInfo;
  const queryClient = useQueryClient();

  const { mutate: updateProfile, isLoading: isUpdatingProfile } = useUpdateProfile();
  const { mutate: changePassword, isLoading: isChangingPassword } = useChangePassword();

  const [openUpdateUser, setOpenUpdateUser] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  // const [openPreferences, setOpenPreferences] = useState(false);

  const route = matchRoute(pathname);
  // const currentLanguage = i18n.language as 'vi' | 'en-US';

  const profileOptions: ItemType[] = useMemo(
    () => [
      /* currentLanguage === 'vi'
      ? {
          label: t('Tiếng việt'),
          key: 'language',
          onClick: () => {
            localStorage.setItem('defaultLanguage', LANGUAGES.US);
            i18n.changeLanguage(LANGUAGES.US);
          },
          icon: <Icon name="vi" className="text-3xl" />,
        }
      : {
          label: t('English'),
          key: 'language',
          onClick: () => {
            localStorage.setItem('defaultLanguage', LANGUAGES.VI);
            i18n.changeLanguage(LANGUAGES.VI);
          },
          icon: <Icon name="en-US" className="text-3xl" />,
        }, */
      {
        label: t('Update Profile'),
        key: 'profile',
        icon: <UserOutlined className="text-3xl" />,
        onClick: () => setOpenUpdateUser(true),
      },
      {
        label: t('Change Password'),
        key: 'password',
        icon: <KeyOutlined className="text-3xl" />,
        onClick: () => setOpenChangePassword(true),
      },
      /* {
      label: t('Preferences'),
      key: 'preferences',
      icon: <SettingsOutlined className="text-3xl" />,
      onClick: () => setOpenPreferences(true),
    }, */
      {
        label: t('Help'),
        key: 'document',
        icon: <HelpOutlined className="text-3xl" />,
        onClick: () => window.open('/document', '_blank'),
      },
      userInfo?.username
        ? {
            label: t('Logout'),
            key: 'logout',
            icon: <PowerOutlined className="text-3xl" />,
            onClick: async () => await actionLogout(),
          }
        : {
            label: t('Login'),
            key: 'login',
            icon: <LoginOutlined className="text-3xl" />,
            onClick: () => requestLogin(true),
          },
    ],
    [t, userInfo?.username]
  );

  const formatItems = useMemo(() => {
    const newItems =
      items ||
      [
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_studylist]) && {
            label: t('Studylist'),
            key: '/studylist',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INITIAL_FILTER_STUDYLIST,
                ...INIT_PAGINATION_PARAM,
                ...INITIAL_SORTER_STUDYLIST,
              }) as ParamsStudyList;

              queryClient.prefetchQuery([queryKey.studies_interval, initParams], () =>
                fetchStudyList(initParams)
              );
              queryClient.prefetchQuery([queryKey.filter_tree], () => getFiltersTree());

              return import('#pages/studylist');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_worklist]) && {
            label: t('Worklist'),
            key: '/worklist',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INITIAL_SORTER_ORDER,
                ...INIT_PAGINATION_PARAM,
              });

              queryClient.prefetchQuery([queryKey.orderWorklist_interval, initParams], () =>
                getOrderWorklist(initParams)
              );

              return import('#pages/order-worklist');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_order]) && {
            label: t('Order'),
            key: '/order',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INITIAL_SORTER_ORDER,
                ...INIT_PAGINATION_PARAM,
              });

              queryClient.prefetchQuery([queryKey.orders_interval, initParams], () =>
                getOrders(initParams)
              );

              return import('#pages/order');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_assignedlist]) && {
            label: t('Assigned'),
            key: '/assigned',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INITIAL_SORTER_ASSIGNED,
                ...INIT_PAGINATION_PARAM,
              });

              queryClient.prefetchQuery([queryKey.assigns_interval, initParams], () =>
                getAssigns(initParams)
              );

              return import('#pages/assigned');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_sharedlist]) && {
            label: t('Shared'),
            key: '/share',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INIT_FILTER_SHARE,
                ...INIT_PAGINATION_PARAM,
              }) as Parameters<typeof getShares>[0];
              const prefetchList = [
                {
                  queryKey: [queryKey.share, initParams],
                  queryFn: () => getShares(initParams),
                },
                prefetchTemplate[queryKey.account],
                prefetchTemplate[queryKey.institution],
              ];

              getPrefetch(queryClient, prefetchList);

              return import('#pages/share');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_patient]) && {
            label: t('Patient'),
            key: '/patient',
            preloadable: lazyWithPreload(() => {
              return import('#pages/patient');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.admin_settings]) && {
            label: t('Administration'),
            key: '/admin/account',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INIT_FILTER_ACCOUNT,
                ...INIT_PAGINATION_PARAM,
              });
              const prefetchList = [
                {
                  queryKey: [queryKey.account, initParams],
                  queryFn: () => getAccounts(initParams),
                },
                prefetchTemplate[queryKey.roles],
                prefetchTemplate[queryKey.account],
                prefetchTemplate[queryKey.institution],
              ];

              getPrefetch(queryClient, prefetchList);

              return import('#pages/admin-settings/pages/account');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.user_settings]) && {
            label: t('User Settings'),
            key: '/user/layout-report',
            preloadable: lazyWithPreload(() => {
              const initParams = sortObjectKeys({
                ...INIT_FILTER_LAYOUT,
                ...INIT_PAGINATION_PARAM,
              });
              const prefetchList = [
                {
                  queryKey: [queryKey.layout_report, initParams],
                  queryFn: () => getLayouts(initParams),
                },
                prefetchTemplate[queryKey.account],
                prefetchTemplate[queryKey.institution],
              ];

              getPrefetch(queryClient, prefetchList);

              return import('#pages/user-settings/pages/layout-report');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_dashboard]) && {
            label: t('Dashboard'),
            key: '/dashboard/performance-by-radiologist',
            preloadable: lazyWithPreload(() => {
              const prefetchList = [
                prefetchTemplate[queryKey.institution],
                prefetchTemplate[queryKey.account],
              ];

              getPrefetch(queryClient, prefetchList);

              return import('#pages/dashboard/pages/performance-by-radiologist');
            }),
          }),
        },
        {
          ...(keycloak.hasOneOfPerm([PERMISSIONS.page.view_upload]) && {
            label: t('Create Study'),
            key: '/upload',
            preloadable: lazyWithPreload(() => {
              // const prefetchList = [
              //   prefetchTemplate[queryKey.institution],
              //   prefetchTemplate[queryKey.account],
              // ];

              // getPrefetch(queryClient, prefetchList);

              return import('#pages/upload');
            }),
          }),
        },
      ].filter((item) => !isEmptyObject(item));

    return newItems.map((item) => {
      return {
        ...item,
        onClick: () => navigate(`${item.key}`),
        className: 'hover:text-primary-light hover:border-primary-light',
      };
    });
  }, [items, userPermissions, keycloak, navigate, queryClient, t]);

  usePreloadLazy(`.${menuClass}`, 'ant-menu-item-active', (element) => {
    const key = element.querySelector('.ant-menu-title-content').textContent;
    const ComponentToPreload = formatItems.find((item) => item.label === key)?.preloadable;

    ComponentToPreload?.preload();
  });

  // const currentLang = i18n.availableLanguages.filter((lang) => lang.value === i18n.language);

  const handleUpdateProfile = useCallback(
    (profile) => {
      updateProfile(
        {
          id: userInfo?.id,
          data: profile,
        },
        {
          onSuccess: (res) => {
            console.log('Profile updated', res);
            message.success(t(MESSAGE.SUCCESS.SCD_4001));
            keycloak.setUserInfo(res?.data);
            setOpenUpdateUser(false);
          },
          onError: (error: any) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              MESSAGE.ERROR.EMSG_0001;

            message.error(t(error_message));
          },
        }
      );
    },
    [keycloak, message, t, updateProfile, userInfo?.id]
  );

  const handleChangePassword = useCallback(
    (values) => {
      changePassword(values, {
        onSuccess: (res) => {
          console.log('Password updated', res);
          message.success(t(MESSAGE.SUCCESS.SCD_4001));
          setOpenChangePassword(false);
        },
        onError: (error) => {
          const error_message =
            (error?.response?.data?.error_code &&
              MESSAGE.ERROR[error?.response?.data?.error_code]) ||
            MESSAGE.ERROR.EMSG_0001;

          message.error(t(error_message));
        },
      });
    },
    [changePassword, message, t]
  );

  const FullName = useMemo(
    () => userInfo?.fullname || userInfo?.username,
    [userInfo?.fullname, userInfo?.username]
  );

  const MenuItems = useMemo(
    () => [...formatItems].map((item) => ({ ...item, preloadable: null })),
    [formatItems]
  );

  const handleCancelProfile = useCallback(() => setOpenUpdateUser(false), []);
  const handleCancelPassword = useCallback(() => setOpenChangePassword(false), []);

  return (
    <header className="flex h-[45px] items-center justify-between bg-secondary-lighter pr-2 shadow dark:bg-secondary-dark md:pr-4">
      <section className="flex w-0 flex-auto items-center overflow-hidden">
        <Link to="/" className="pl-2 md:basis-[220px] md:pl-0">
          <div className="flex justify-center">
            <Logo />
          </div>
        </Link>

        <Menu
          items={MenuItems}
          mode="horizontal"
          selectedKeys={[route.path]}
          className={`${menuClass} flex-auto overflow-hidden border-0`}
        />
      </section>

      <section className="ml-2 flex items-center space-x-2 md:ml-4 md:space-x-4">
        {/* <div onClick={() => navigate('/dashboard/overview')} className="mr-3 cursor-pointer">
          <Icon name="line-chart" className="text-4xl" />
        </div> */}

        {/* <ThemeSwitcher /> */}
        <ToggleSplitScreen iframe={iframe} />
        <LanguageSwitcher />

        {canLogin && <Dropdown
          menu={{ items: profileOptions }}
          placement="bottomRight"
          className="header-dropdown-title ml-2"
          overlayClassName="vindr-dropdown header-dropdown hover:cursor-pointer"
        >
          <div className="user-info flex items-center hover:cursor-pointer">
            <AccountOutlined
              className={classnames('mr-2 text-4xl', {
                'animate-pulse': !FullName,
              })}
            />
            <Username name={FullName} />
          </div>
        </Dropdown>}
        {openUpdateUser && (
          <Profile
            isLoading={isUpdatingProfile}
            onCancel={handleCancelProfile}
            profile={userInfo}
            onSubmit={handleUpdateProfile}
          />
        )}
        {openChangePassword && (
          <ChangePassword
            isLoading={isChangingPassword}
            onCancel={handleCancelPassword}
            onSubmit={handleChangePassword}
          />
        )}
        {/* {openPreferences && (
          <PreferencesSetting open={openPreferences} onCancel={() => setOpenPreferences(false)} />
        )} */}
      </section>
    </header>
  );
};

export default memo(Header, (prev, next) => isEqual(prev.items, next.items));
